import type {
  Stripe,
  StripeConstructor,
  StripeElements,
  StripeExpressCheckoutElement,
  StripeExpressCheckoutElementConfirmEvent,
  StripeLinkAuthenticationElement,
  PaymentMethod as StripePaymentMethod,
} from "@stripe/stripe-js";
import { type AddressPayload, clean, fromStripeAddress } from "../utils/wallet-billing-address";

// Stripe.js must be loaded via the external <script> tag for PCI compliance,
// exactly like the Apple/Google Pay adapters. We read window.Stripe — we never
// bundle the SDK.
declare global {
  interface Window {
    Stripe?: StripeConstructor;
  }
}

export enum StripeLinkMockScenario {
  None = "none",
  Success = "success",
  Cancelled = "cancelled",
}

export type StripeLinkMode = "payment" | "setup";

export interface StripeLinkElementsConfig {
  /** "payment" charges; "setup" saves the method without a charge. */
  mode: StripeLinkMode;
  /** Atomic amount (cents). Required by Stripe Elements in payment mode. */
  amount: number;
  /** Lowercase ISO currency, e.g. "usd". */
  currency: string;
  /**
   * Backend-decided (`/stripe-link/start` `save_for_future_use`): true when the
   * saved Link PM must carry the off-session mandate — i.e. the checkout has
   * recurring line items, so renewals will charge this PM off-session. A Link
   * PM minted without `setup_future_usage: "off_session"` is declined by Stripe
   * on off-session renewal charges. Always implied in setup mode.
   */
  saveForFutureUse?: boolean;
}

export type StripeLinkBillingDetails = {
  email?: string;
  name?: string;
  phone?: string;
  // Already in the backend's AddressCreate shape (`fromStripeAddress`).
  address?: AddressPayload;
};

/**
 * Result of the shopper confirming via the Link express button: a
 * pre-authenticated PaymentMethod id (`pm_...`) plus any billing details Link
 * surfaced. The shell POSTs the id to `/stripe-link/confirm`.
 */
export type StripeLinkConfirmResult =
  | {
      success: true;
      paymentMethodId: string;
      billingDetails?: StripeLinkBillingDetails;
      /**
       * The total the Link window showed when the shopper opened it. Echo this,
       * not the latest armed total: a refresh or re-init while the window was
       * open changes only what we would charge, never what the window shows.
       */
      shownSheet?: StripeLinkSheet;
    }
  | { success: false; cancelled: true }
  | { success: false; error: string };

/**
 * One setupElements() run. `windowOpen` is true from the button click until the
 * Link window closes. `parked` holds that run's element when the merchant tore
 * it down while the window was open: Stripe answers the window through the
 * element's iframe, so destroying it leaves the window on "Processing..." even
 * after paymentFailed(). The element stays hidden until the window closes.
 * `closeWindow` runs that run's window-closed path (release a parked element,
 * apply a deferred refresh). `armed` is the total this run's Elements group
 * holds now; `shown` is the total it held when the window opened.
 */
/** Shown when a Link confirm comes from a window the checkout has since replaced or closed. */
const STALE_LINK_WINDOW_ERROR = "The checkout changed while Link was open. Please try again.";

export type StripeLinkSheet = { amount: number; currency: string };

type LinkSetup = {
  windowOpen: boolean;
  armed: StripeLinkSheet;
  shown: StripeLinkSheet | null;
  parked: { element: StripeExpressCheckoutElement; container: HTMLElement } | null;
  closeWindow: () => void;
};

/**
 * Stripe Link adapter — Option 1 (design §4): an **isolated, Link-only** Stripe
 * `expressCheckout` element, mounted beside the VGS Collect iframes. It is
 * configured `paymentMethods: { applePay/googlePay/paypal/amazonPay: 'never',
 * link: 'auto' }` so it renders ONLY the Link button and never suppresses the
 * existing `paymentRequest()` Apple/Google Pay buttons.
 *
 * A hidden `linkAuthentication` element detects an authenticated Link session
 * silently — that boolean drives express-vs-tile in the wrapper via the pure
 * `should_show_link_express_button` port.
 */
export class StripeLinkAdapter {
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private elementsMode: StripeLinkMode | null = null;
  private expressElement: StripeExpressCheckoutElement | null = null;
  private linkAuthElement: StripeLinkAuthenticationElement | null = null;
  private readonly mockScenario: StripeLinkMockScenario;
  private mountContainer: HTMLElement | null = null;
  private authContainer: HTMLElement | null = null;
  private authObserver: MutationObserver | null = null;
  private confirmHandler: ((result: StripeLinkConfirmResult) => void) | null = null;
  private windowClosedHandler: (() => void) | null = null;
  // The newest setupElements() run (this.elements is its group). Never cleared:
  // it is NOT evidence that the instance is still in use (see instanceLive).
  private currentSetup: LinkSetup | null = null;
  // The run whose express button is on the page now (null once unmounted).
  private mountedSetup: LinkSetup | null = null;
  // False once the wrapper is torn down with no re-init after it: the merchant
  // dropped this instance (and its session), so no window of it may pay.
  private instanceLive = true;

  constructor(mockScenario?: StripeLinkMockScenario) {
    this.mockScenario = mockScenario ?? StripeLinkMockScenario.None;
  }

  private get isMock(): boolean {
    return this.mockScenario !== StripeLinkMockScenario.None;
  }

  /** Read window.Stripe (loaded via external script tag) and construct the client. */
  initialize(publishableKey: string): boolean {
    if (this.isMock) return true;
    if (!window.Stripe) {
      console.error("[StripeLink] Stripe.js not loaded");
      return false;
    }
    this.stripe = window.Stripe(publishableKey);
    return this.stripe !== null;
  }

  /**
   * Build the Link-only Elements group. Mode/amount/currency come from
   * `/stripe-link/start`. Does not mount anything yet.
   */
  setupElements(config: StripeLinkElementsConfig): { success: boolean; error?: string } {
    if (this.isMock) return { success: true };
    if (!this.stripe) return { success: false, error: "Stripe not initialized" };

    // Elements in setup mode does not take an amount; payment mode requires it.
    this.elementsMode = config.mode;
    this.elements =
      config.mode === "setup"
        ? this.stripe.elements({
            mode: "setup",
            currency: config.currency.toLowerCase(),
            setupFutureUsage: "off_session",
            paymentMethodCreation: "manual",
          })
        : this.stripe.elements({
            mode: "payment",
            amount: config.amount,
            currency: config.currency.toLowerCase(),
            // A recurring checkout charges now AND saves the PM for renewals —
            // the mandate must be baked into the PM at creation or off-session
            // renewal charges are declined (backend decides via /start).
            ...(config.saveForFutureUse ? { setupFutureUsage: "off_session" as const } : {}),
            paymentMethodCreation: "manual",
          });

    // Link-only express button — never renders or suppresses Apple/Google Pay.
    this.expressElement = this.elements.create("expressCheckout", {
      paymentMethods: {
        amazonPay: "never",
        applePay: "never",
        googlePay: "never",
        paypal: "never",
        link: "auto",
      },
      // PAY-1553: Stripe only returns payer identity on the `confirm` event for
      // the fields the element was asked to collect. Without this the event
      // carries NO billingDetails, and a link-type PM's `billing_details` comes
      // back empty — so every Link payment minted an anonymous customer, with
      // no email to send receipts/dunning to and nothing to dedupe repeat
      // purchases on.
      //
      // Both flags are required to get ANY identity back. Stripe's BillingDetails
      // declares `name` and `address` as required members and only surfaces the
      // object when it can fill them, so `billingAddressRequired: false` drops
      // the whole thing — email included — and we are back to anonymous
      // customers. The merchant's Checkout > Fields billing-address setting is
      // therefore honoured server-side (the address is discarded on /confirm
      // when they have it off) rather than by not collecting it here, which
      // would cost the email too.
      //
      // Phone is deliberately NOT requested: nothing in the product reads
      // `customer.phone` beyond read-only projections, and any field Stripe
      // cannot fill from the Link account is collected in the payment
      // interface — an extra step on the highest-intent moment in the funnel.
      emailRequired: true,
      billingAddressRequired: true,
    });

    // Bind the confirm handler to THIS run's client and Elements group. A
    // re-init replaces this.stripe/this.elements, and a confirm from a button
    // built by an earlier run must not create its payment method from a group
    // whose element is not mounted (Stripe throws "Could not find a ready
    // element to create a payment method from").
    const stripe = this.stripe;
    const elements = this.elements;
    const setup: LinkSetup = {
      windowOpen: false,
      armed: { amount: config.amount, currency: config.currency },
      shown: null,
      parked: null,
      closeWindow: () => windowClosed(),
    };
    this.currentSetup = setup;

    // The Link window closed: release the element if a teardown parked it.
    const windowClosed = (): void => {
      setup.windowOpen = false;
      if (setup.parked) {
        setup.parked.element.unmount();
        setup.parked.container.remove();
        setup.parked = null;
      }
      // A parked run's window closing must not apply a deferred total while the
      // live button's window is still open.
      if (!this.isWindowOpen()) this.windowClosedHandler?.();
    };

    this.expressElement.on("click", (event) => {
      setup.windowOpen = true;
      setup.shown = setup.armed;
      // Resolving with no options keeps Link as the only flow.
      event.resolve();
    });
    // Assumption: once click resolves, Stripe always ends the window with
    // "cancel" (shopper closes it, including after paymentFailed) or with
    // "confirm". If the element fails to load instead, no window can be open,
    // so "loaderror" clears the flag too: a stuck flag would defer every
    // re-arm and park a torn-down element for the life of the page.
    this.expressElement.on("cancel", windowClosed);
    this.expressElement.on("loaderror", windowClosed);

    // Every failure must call paymentFailed(): until Stripe hears back, the Link
    // window spins on "Processing..." with no way out for the shopper. The
    // window stays open to show the error, so the element is released on the
    // "cancel" that closing it fires.
    // No `message`: Link ignores it and always shows its generic "Payment failed"
    // (checked in a real Link window).
    const fail = (event: StripeExpressCheckoutElementConfirmEvent, error: string): void => {
      event.paymentFailed({ reason: "fail" });
      this.confirmHandler?.({ success: false, error });
    };

    // May this run's window still pay? The run must own an open window: Stripe
    // always fires "click" (which opens it) before "confirm", so a confirm with
    // no open window comes from a window this run gave up (a button move). The
    // instance must be live: a teardown with no re-init after it means the
    // merchant dropped this instance (for example for a new checkout session),
    // so fail instead of charging it. And the run must be on the page, or
    // parked by a teardown of a still-live instance: one instance has one
    // session and token, so a re-init (how a merchant passes customerInfo
    // learned on the click's blur) keeps the session, and the shopper's
    // authorization in the window stands, whether or not the re-init's /start
    // has answered yet.
    const canPay = (): boolean =>
      setup.windowOpen && this.instanceLive && (this.mountedSetup === setup || setup.parked !== null);

    this.expressElement.on("confirm", async (event) => {
      if (!canPay()) {
        fail(event, STALE_LINK_WINDOW_ERROR);
        return;
      }
      let result: Awaited<ReturnType<Stripe["createPaymentMethod"]>>;
      try {
        result = await stripe.createPaymentMethod({ elements });
      } catch (err) {
        // Stripe throws (rather than returning `error`) for integration errors.
        fail(event, err instanceof Error ? err.message : "Stripe Link failed to create a payment method");
        return;
      }
      // Same checks, for a teardown, re-init or button move that landed during
      // the await. Only windowClosed() and unmount() clear the open-window flag;
      // this handler calls windowClosed() only after the confirm below.
      if (!canPay()) {
        fail(event, STALE_LINK_WINDOW_ERROR);
        return;
      }
      if (result.error || !result.paymentMethod) {
        fail(event, result.error?.message ?? "Stripe Link failed to create a payment method");
        return;
      }
      this.confirmHandler?.({
        success: true,
        paymentMethodId: result.paymentMethod.id,
        billingDetails: extractBillingDetails(event, result.paymentMethod),
        shownSheet: setup.shown ?? setup.armed,
      });
      // Stripe closes the Link window once the payment method exists.
      windowClosed();
    });

    this.linkAuthElement = this.elements.create("linkAuthentication");
    return { success: true };
  }

  /**
   * Mark the instance dropped (`false`, on a teardown) or in use again (`true`,
   * on an init). A parked window of a dropped instance can never pay.
   */
  setInstanceLive(live: boolean): void {
    this.instanceLive = live;
  }

  /**
   * True while a Link window of the live Elements group is open: the button on
   * the page, or a button parked by a re-init that has not built its new run
   * yet (until then this.elements is still the parked run's group).
   */
  isWindowOpen(): boolean {
    return (this.mountedSetup?.windowOpen ?? false) || (this.currentSetup?.windowOpen ?? false);
  }

  /** Register the callback invoked each time a Link window closes. */
  onWindowClosed(handler: () => void): void {
    this.windowClosedHandler = handler;
  }

  /**
   * Change the armed total on the live Elements group without remounting the
   * button. Stripe cannot switch payment <-> setup in place, so a mode change
   * needs a re-init. An open Link window keeps showing the total it opened
   * with, so call this only while the window is closed.
   */
  updateAmount(config: StripeLinkElementsConfig): { success: boolean; error?: string } {
    if (this.isMock) return { success: true };
    if (!this.elements) return { success: false, error: "Stripe Elements not ready" };
    if (config.mode !== this.elementsMode) {
      return { success: false, error: "Checkout mode changed; re-initialize Stripe Link" };
    }
    // Setup mode carries no amount.
    if (config.mode === "payment") {
      this.elements.update({ amount: config.amount, currency: config.currency.toLowerCase() });
    }
    if (this.currentSetup) this.currentSetup.armed = { amount: config.amount, currency: config.currency };
    return { success: true };
  }

  /** Register the callback invoked when the shopper confirms with Link. */
  onConfirm(handler: (result: StripeLinkConfirmResult) => void): void {
    this.confirmHandler = handler;
  }

  /** Mount the Link express button into a Link-owned <div> under `selector`. */
  mountExpressButton(selector: string): { success: boolean; error?: string } {
    if (this.isMock) return { success: true };
    if (!this.expressElement) return { success: false, error: "Express element not set up" };
    const parent = document.querySelector(selector);
    if (!parent) return { success: false, error: `Mount target not found: ${selector}` };
    // The Link window is open on this very button: keep it. Stripe answers the
    // window through the element's iframe, so a remount (a merchant effect that
    // re-runs on the click's blur) would leave the window on "Processing...".
    // A mount into another target, or a remount that finds the button detached,
    // moves the button now and gives up that window, so run its close path (a
    // deferred refresh applies). Assumption, not yet checked in a real Link
    // window: a moved or detached iframe's window never sends "cancel".
    if (this.mountContainer && this.mountedSetup === this.currentSetup && this.mountedSetup?.windowOpen) {
      if (this.mountContainer.isConnected && parent.contains(this.mountContainer)) {
        return { success: true };
      }
      this.mountedSetup.closeWindow();
    }
    // Idempotent: tear down any prior mount first so a repeated call (React
    // strict-mode double-invoke, or a dependency change) replaces the button
    // instead of stacking a second one.
    if (this.mountContainer) {
      this.expressElement.unmount();
      this.mountContainer.remove();
      this.mountContainer = null;
    }
    // Own a dedicated child node so we never touch the VGS PAN/expiry/CVC DOM.
    const container = document.createElement("div");
    parent.appendChild(container);
    this.mountContainer = container;
    this.expressElement.mount(container);
    this.mountedSetup = this.currentSetup;
    return { success: true };
  }

  unmount(): void {
    const setup = this.mountedSetup;
    this.mountedSetup = null;
    // A detached button's window is assumed lost (no "cancel" will come; not yet
    // checked in a real Link window), so parking it could hold the element for
    // the life of the page: destroy it at once.
    if (setup && !this.mountContainer?.isConnected) setup.windowOpen = false;
    if (setup?.windowOpen && this.expressElement && this.mountContainer) {
      // The Link window is open on this button: hide it instead of destroying
      // it, so its confirm can still close the window (see LinkSetup).
      this.mountContainer.style.display = "none";
      setup.parked = { element: this.expressElement, container: this.mountContainer };
      // Forget it here, so a second unmount() (teardown + cleanup) can't destroy it.
      this.expressElement = null;
    } else {
      this.expressElement?.unmount();
      this.mountContainer?.remove();
    }
    this.linkAuthElement?.unmount();
    this.authObserver?.disconnect();
    this.authObserver = null;
    this.authContainer?.remove();
    this.mountContainer = null;
    this.authContainer = null;
  }

  /**
   * Silently detect whether the shopper has an authenticated Link session by
   * mounting a hidden `linkAuthentication` element off-screen and watching its
   * `change` event + iframe growth. Drives express-vs-tile.
   */
  detectAuthenticatedSession(onChange: (authenticated: boolean) => void): void {
    if (this.isMock) {
      // Mock: treat "success" as an authenticated session so express renders.
      onChange(this.mockScenario === StripeLinkMockScenario.Success);
      return;
    }
    if (!this.linkAuthElement) {
      onChange(false);
      return;
    }
    // Idempotent: tear down any prior detection first so a repeated call (e.g. a
    // same-generation double init) doesn't orphan the previous hidden container
    // or leak its observer — mirrors mountExpressButton().
    this.authObserver?.disconnect();
    this.authObserver = null;
    this.authContainer?.remove();
    this.authContainer = null;
    const container = document.createElement("div");
    Object.assign(container.style, {
      visibility: "hidden",
      position: "absolute",
      top: "-1000px",
      left: "-1000px",
      zIndex: "-1",
    });
    document.body.appendChild(container);
    this.authContainer = container;
    this.linkAuthElement.mount(container);

    let authenticated = false;
    this.linkAuthElement.on("change", (e) => {
      authenticated = e.complete || !!e.value.email;
      onChange(authenticated);
    });

    // Fallback heuristic: an authenticated Link iframe grows past ~160px. Stripe
    // injects the iframe asynchronously, so we can't query it synchronously here
    // (it isn't in the DOM yet). Watch the container for the iframe to appear,
    // then observe its style changes for the growth signal.
    const observer = new MutationObserver(() => {
      const iframe = container.querySelector("iframe");
      if (!iframe) return;
      const tallEnough = iframe.offsetHeight > 160;
      onChange(authenticated || tallEnough);
    });
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    this.authObserver = observer;
  }

  /** Mock-only: synthesize a confirm for unit tests / mock scenarios. */
  mockConfirm(): StripeLinkConfirmResult {
    if (this.mockScenario === StripeLinkMockScenario.Cancelled) {
      return { success: false, cancelled: true };
    }
    return {
      success: true,
      paymentMethodId: "pm_mock_stripe_link",
      billingDetails: {
        // Mirrors what the real element collects: email + name + address, no
        // phone (phoneNumberRequired is deliberately not set).
        email: "test@example.com",
        name: "Test User",
        address: {
          line1: "1 Test Street",
          city: "San Francisco",
          state: "CA",
          zip_code: "94105",
          country: "US",
        },
      },
    };
  }
}

/**
 * Merge the payer identity Link surfaced, preferring the `confirm` event (which
 * carries exactly the fields the element was configured to collect) and falling
 * back per-field to the created PaymentMethod's `billing_details`.
 *
 * PAY-1553: reading only the PM was the original bug — a link-type PM's
 * `billing_details` comes back empty, so name/phone/address were never captured
 * and email was frequently null too.
 */
function extractBillingDetails(
  event: StripeExpressCheckoutElementConfirmEvent,
  pm: StripePaymentMethod,
): StripeLinkBillingDetails {
  const fromEvent = event.billingDetails;
  const fromPm = pm.billing_details;

  return {
    // Scalars merge per field — mixing sources is harmless for these.
    email: clean(fromEvent?.email) ?? clean(fromPm?.email),
    name: clean(fromEvent?.name) ?? clean(fromPm?.name),
    phone: clean(fromEvent?.phone) ?? clean(fromPm?.phone),
    // The address does NOT: these are two independent addresses, and filling a
    // gap in one from the other splices a chimera (a London street with a San
    // Francisco postcode) that then gets persisted. Take whichever source has
    // an address, whole. Mirrors the backend filling first/last name as a unit.
    address: fromStripeAddress(fromEvent?.address) ?? fromStripeAddress(fromPm?.address),
  };
}
